import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Label, Input, Spinner } from 'reactstrap';
import { profileApi } from '../../services/api';

interface CountrySelectionModalProps {
    isOpen: boolean;
    onCountrySelected: (country: string) => void;
}

// Common countries list - UK first as primary market
const COUNTRIES = [
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'AT', name: 'Austria' },
    { code: 'IE', name: 'Ireland' },
    { code: 'PL', name: 'Poland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'NO', name: 'Norway' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'GR', name: 'Greece' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'HU', name: 'Hungary' },
    { code: 'RO', name: 'Romania' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'HR', name: 'Croatia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LV', name: 'Latvia' },
    { code: 'EE', name: 'Estonia' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'MT', name: 'Malta' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'CA', name: 'Canada' },
    { code: 'AU', name: 'Australia' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'JP', name: 'Japan' },
    { code: 'KR', name: 'South Korea' },
    { code: 'CN', name: 'China' },
    { code: 'IN', name: 'India' },
    { code: 'BR', name: 'Brazil' },
    { code: 'MX', name: 'Mexico' },
    { code: 'TR', name: 'Turkey' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SG', name: 'Singapore' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'IL', name: 'Israel' },
    { code: 'RU', name: 'Russia' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'AR', name: 'Argentina' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colombia' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'TH', name: 'Thailand' },
    { code: 'PH', name: 'Philippines' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'EG', name: 'Egypt' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'OTHER', name: 'Other' },
];

const CountrySelectionModal: React.FC<CountrySelectionModalProps> = ({ isOpen, onCountrySelected }) => {
    const [selectedCountry, setSelectedCountry] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        if (!selectedCountry) {
            setError('Please select your country');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await profileApi.updateProfile({ country: selectedCountry });
            onCountrySelected(selectedCountry);
        } catch (err: any) {
            setError(err.message || 'Failed to save country. Please try again.');
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} centered backdrop="static" keyboard={false}>
            <ModalHeader className="border-0 pb-0">
                <div className="d-flex align-items-center">
                    <i className="ri-global-line text-primary me-2" style={{ fontSize: '24px' }}></i>
                    <span>Select Your Country</span>
                </div>
            </ModalHeader>
            <ModalBody className="pt-2">
                <p className="text-muted mb-3">
                    Please select your country of residence. This information is required for billing and tax purposes.
                </p>

                <div className="mb-3">
                    <Label htmlFor="country-select" className="form-label">
                        Country <span className="text-danger">*</span>
                    </Label>
                    <Input
                        type="select"
                        id="country-select"
                        value={selectedCountry}
                        onChange={(e) => {
                            setSelectedCountry(e.target.value);
                            setError(null);
                        }}
                        className={error ? 'is-invalid' : ''}
                    >
                        <option value="">-- Select Country --</option>
                        {COUNTRIES.map((country) => (
                            <option key={country.code} value={country.code}>
                                {country.name}
                            </option>
                        ))}
                    </Input>
                    {error && <div className="invalid-feedback d-block">{error}</div>}
                </div>

                <div className="alert alert-info mb-0 d-flex align-items-start">
                    <i className="ri-information-line me-2 mt-1"></i>
                    <div>
                        <small>
                            <strong>Note:</strong> If you are based in the United Kingdom, VAT (20%) will be applied to your invoices.
                            Non-UK customers are exempt from UK VAT.
                        </small>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter className="border-0 pt-0">
                <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading || !selectedCountry}
                    className="w-100"
                >
                    {loading ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <i className="ri-check-line me-1"></i>
                            Continue
                        </>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CountrySelectionModal;
